import  { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Grid,
  
  Typography,
  TextField,
  FormControl,

  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Card,
  CardContent,
  CardHeader,
  Box,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Switch,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Shield,
  Key,
  Delete,
  Phone,
  Public,
  
  Share,
  Warning,
  CheckCircle,
  Settings,
  
} from '@mui/icons-material';

const SecurityPage = () => {
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [accountPrivacy, setAccountPrivacy] = useState('public');
  const [dataSharing, setDataSharing] = useState('yes');
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const handlePasswordChange = (field) => (event) => {
    setPasswords({ ...passwords, [field]: event.target.value });
  };

  const handlePasswordVisibility = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    },
    hover: {
      y: -2,
      transition: { duration: 0.2 }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ padding: '24px', backgroundColor: '#F8EEDB', minHeight: '100vh' }}
    >
      <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <motion.div variants={cardVariants}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50', mb: 1 }}>
              Security Settings
            </Typography>
            <Typography variant="body1" sx={{ color: '#5D6D7E' }}>
              Manage your account security and privacy preferences
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {/* Password Change Card */}
          <Grid item xs={12} lg={8}>
            <motion.div variants={cardVariants} whileHover="hover">
              <Card sx={{ height: '100%' }}>
                <CardHeader
                  avatar={<Key sx={{ color: '#EB5A3C' }} />}
                  title="Change Password"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  sx={{ backgroundColor: '#FFF6E9', borderBottom: '1px solid #E6D4B7' }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showPassword.current ? 'text' : 'password'}
                      value={passwords.current}
                      onChange={handlePasswordChange('current')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handlePasswordVisibility('current')}
                              edge="end"
                            >
                              {showPassword.current ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPassword.new ? 'text' : 'password'}
                      value={passwords.new}
                      onChange={handlePasswordChange('new')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handlePasswordVisibility('new')}
                              edge="end"
                            >
                              {showPassword.new ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPassword.confirm ? 'text' : 'password'}
                      value={passwords.confirm}
                      onChange={handlePasswordChange('confirm')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handlePasswordVisibility('confirm')}
                              edge="end"
                            >
                              {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: '#EB5A3C',
                          '&:hover': { backgroundColor: '#C54428' }
                        }}
                      >
                        Update Password
                      </Button>
                      <Button variant="outlined" color="inherit">
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Two-Factor Authentication Card */}
          <Grid item xs={12} lg={4}>
            <motion.div variants={cardVariants} whileHover="hover">
              <Card sx={{ height: '100%' }}>
                <CardHeader
                  avatar={<Shield sx={{ color: '#EB5A3C' }} />}
                  title="Two-Factor Authentication"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  sx={{ backgroundColor: '#FFF6E9', borderBottom: '1px solid #E6D4B7' }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#5D6D7E' }}>
                    Add an extra layer of security to your account with SMS verification.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Enable 2FA
                    </Typography>
                    <Switch
                      checked={twoFactorEnabled}
                      onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#EB5A3C',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#EB5A3C',
                        },
                      }}
                    />
                  </Box>
                  
                  {twoFactorEnabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <TextField
                        fullWidth
                        label="SMS Number"
                        placeholder="+1 (555) 123-4567"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone sx={{ color: '#EB5A3C' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                      
                      <Alert severity="success" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          2FA is currently enabled for your account
                        </Typography>
                      </Alert>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Security Preferences Card */}
          <Grid item xs={12} lg={8}>
            <motion.div variants={cardVariants} whileHover="hover">
              <Card>
                <CardHeader
                  avatar={<Settings sx={{ color: '#EB5A3C' }} />}
                  title="Security Preferences"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  sx={{ backgroundColor: '#FFF6E9', borderBottom: '1px solid #E6D4B7' }}
                />
                <CardContent sx={{ p: 3 }}>
                  {/* Account Privacy Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Public sx={{ color: '#EB5A3C', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Account Privacy
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 3, color: '#5D6D7E' }}>
                      Control who can see your profile information and posts.
                    </Typography>
                    
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={accountPrivacy}
                        onChange={(e) => setAccountPrivacy(e.target.value)}
                      >
                        <FormControlLabel
                          value="public"
                          control={<Radio sx={{ color: '#EB5A3C', '&.Mui-checked': { color: '#EB5A3C' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Public Account
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5D6D7E' }}>
                                Posts are visible to all users
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="private"
                          control={<Radio sx={{ color: '#EB5A3C', '&.Mui-checked': { color: '#EB5A3C' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Private Account
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5D6D7E' }}>
                                Posts are visible only to users in your groups
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Data Sharing Section */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Share sx={{ color: '#EB5A3C', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Data Sharing
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 3, color: '#5D6D7E' }}>
                      Help us improve our products by sharing usage data and crash reports.
                    </Typography>
                    
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={dataSharing}
                        onChange={(e) => setDataSharing(e.target.value)}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio sx={{ color: '#EB5A3C', '&.Mui-checked': { color: '#EB5A3C' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Yes, share data
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5D6D7E' }}>
                                Share usage data and crash reports with developers
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio sx={{ color: '#EB5A3C', '&.Mui-checked': { color: '#EB5A3C' } }} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                No, limit data sharing
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5D6D7E' }}>
                                Limit data sharing with app developers
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Account Deletion Card */}
          <Grid item xs={12} lg={4}>
            <motion.div variants={cardVariants} whileHover="hover">
              <Card sx={{ height: '100%', border: '1px solid #FFE5E5' }}>
                <CardHeader
                  avatar={<Warning sx={{ color: '#E74C3C' }} />}
                  title="Danger Zone"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600, color: '#E74C3C' }}
                  sx={{ backgroundColor: '#FFE5E5', borderBottom: '1px solid #E74C3C' }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 3, color: '#5D6D7E' }}>
                    Deleting your account is permanent and cannot be undone. All your data will be lost.
                  </Typography>
                  
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="caption">
                      This action cannot be reversed!
                    </Typography>
                  </Alert>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<Delete />}
                    sx={{
                      borderColor: '#E74C3C',
                      color: '#E74C3C',
                      '&:hover': {
                        backgroundColor: '#FFE5E5',
                        borderColor: '#C0392B'
                      }
                    }}
                  >
                    Delete Account
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Security Status Overview */}
          <Grid item xs={12}>
            <motion.div variants={cardVariants} whileHover="hover">
              <Card>
                <CardHeader
                  avatar={<SecurityIcon sx={{ color: '#EB5A3C' }} />}
                  title="Security Status"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  sx={{ backgroundColor: '#FFF6E9', borderBottom: '1px solid #E6D4B7' }}
                />
                <CardContent sx={{ p: 3 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#27AE60' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Strong Password"
                        secondary="Your password meets all security requirements"
                      />
                      <ListItemSecondaryAction>
                        <Chip label="Active" color="success" size="small" />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: twoFactorEnabled ? '#27AE60' : '#6C757D' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Two-Factor Authentication"
                        secondary={twoFactorEnabled ? "2FA is enabled on your account" : "2FA is disabled"}
                      />
                      <ListItemSecondaryAction>
                        <Chip 
                          label={twoFactorEnabled ? "Enabled" : "Disabled"} 
                          color={twoFactorEnabled ? "success" : "default"} 
                          size="small" 
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#27AE60' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Account Verification"
                        secondary="Your email address has been verified"
                      />
                      <ListItemSecondaryAction>
                        <Chip label="Verified" color="success" size="small" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </motion.div>
  );
};

export default SecurityPage;
